// src/pages/AdminDashboard.tsx - Header kept, blank space removed

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Users,
  Shield,
  UserCog,
  Plus,
  Trash2,
  Edit,
  Save,
  Loader2,
  RefreshCw,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  Settings,
  Database,
  Activity,
  Clock,
  UserCheck,
  UserX,
  Key,
  Briefcase,
  Lock,
  Unlock,
  Server,
  HardDrive,
  Wifi,
  WifiOff,
  FileText,
  Eye,
  ShieldAlert,
  UserPlus,
} from 'lucide-react';

// Helper function for initials
const getInitials = (name: string) => {
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

// User role definitions
const USER_ROLES = [
  { value: 'admin', label: 'Administrator', color: 'bg-red-100 text-red-700', icon: ShieldAlert },
  { value: 'senior_analyst', label: 'Senior Analyst', color: 'bg-purple-100 text-purple-700', icon: UserCog },
  { value: 'analyst', label: 'Analyst', color: 'bg-blue-100 text-blue-700', icon: Users },
  { value: 'founder', label: 'Founder', color: 'bg-green-100 text-green-700', icon: Briefcase },
];

interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  analyst_type: 'business' | 'problem_bank' | null;
  status: 'active' | 'inactive' | 'suspended';
  created_at: string;
  last_login?: string;
}

interface AuditLog {
  id: string;
  user_id: string;
  user_name: string;
  action: string;
  details: string;
  created_at: string;
}

interface SystemStats {
  totalUsers: number;
  activeUsers: number;
  suspendedUsers: number;
  admins: number;
  analysts: number;
  founders: number;
  storageUsed: string;
  apiCallsToday: number;
  version: string;
}

// Stat Card Component
function StatCard({ title, value, description, icon: Icon, color }: any) {
  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          </div>
          <div className={`${color} p-2 rounded-lg text-white`}>
            <Icon className="h-4 w-4" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function AdminDashboard() {
  const { user, refreshUsers } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  
  // Determine which view to show based on URL path
  const getActiveView = () => {
    if (location.pathname.includes('/system')) return 'system';
    if (location.pathname.includes('/audit')) return 'audit';
    return 'users';
  };
  
  const [activeView] = useState(getActiveView());
  
  // User management state
  const [users, setUsers] = useState<User[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  
  // Dialog states
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Form state for create/edit
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'analyst',
    analyst_type: null as string | null,
    password: '',
  });
  
  // Audit logs state
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [showAuditDialog, setShowAuditDialog] = useState(false);
  const [selectedUserLogs, setSelectedUserLogs] = useState<User | null>(null);
  
  // System stats
  const [systemStats, setSystemStats] = useState<SystemStats>({
    totalUsers: 0,
    activeUsers: 0,
    suspendedUsers: 0,
    admins: 0,
    analysts: 0,
    founders: 0,
    storageUsed: '0 MB',
    apiCallsToday: 0,
    version: '1.0.0',
  });
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isRealtimeConnected, setIsRealtimeConnected] = useState(true);

  // Load users from Supabase
  const loadUsers = useCallback(async () => {
    setIsLoadingUsers(true);
    try {
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      const formattedUsers: User[] = (data || []).map(u => ({
        id: u.id,
        email: u.email,
        name: u.name,
        role: u.role,
        analyst_type: u.analyst_type,
        status: u.status || 'active',
        created_at: u.created_at,
        last_login: u.last_login,
      }));
      
      setUsers(formattedUsers);
      updateStats(formattedUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast({ title: 'Error', description: 'Could not load users', variant: 'destructive' });
    } finally {
      setIsLoadingUsers(false);
    }
  }, [toast]);

  const updateStats = (usersList: User[]) => {
    setSystemStats(prev => ({
      ...prev,
      totalUsers: usersList.length,
      activeUsers: usersList.filter(u => u.status === 'active').length,
      suspendedUsers: usersList.filter(u => u.status === 'suspended').length,
      admins: usersList.filter(u => u.role === 'admin').length,
      analysts: usersList.filter(u => u.role === 'analyst' || u.role === 'senior_analyst').length,
      founders: usersList.filter(u => u.role === 'founder').length,
    }));
  };

  // Load audit logs
  const loadAuditLogs = useCallback(async (userId?: string) => {
    setIsLoadingLogs(true);
    try {
      let query = supabase
        .from('audit_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (userId) {
        query = query.eq('user_id', userId);
      }
      
      const { data, error } = await query;
      if (error) throw error;
      
      setAuditLogs(data || []);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setIsLoadingLogs(false);
    }
  }, []);

  // Create new user
  const handleCreateUser = async () => {
    if (!formData.email || !formData.name || !formData.password) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }
    
    setIsProcessing(true);
    try {
      const { error: userError } = await supabase
        .from('users')
        .insert({
          email: formData.email,
          name: formData.name,
          role: formData.role,
          analyst_type: formData.analyst_type === 'null' ? null : formData.analyst_type,
          status: 'active',
          password: formData.password,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      
      if (userError) throw userError;
      
      toast({ title: 'Success', description: `User ${formData.email} created successfully` });
      setCreateUserOpen(false);
      resetForm();
      loadUsers();
    } catch (error: any) {
      console.error('Failed to create user:', error);
      toast({ title: 'Error', description: error.message || 'Failed to create user', variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Update user
  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    setIsProcessing(true);
    try {
      const updates: any = {
        name: formData.name,
        role: formData.role,
        analyst_type: formData.analyst_type === 'null' ? null : formData.analyst_type,
        updated_at: new Date().toISOString(),
      };
      
      const { error } = await supabase
        .from('users')
        .update(updates)
        .eq('id', selectedUser.id);
      
      if (error) throw error;
      
      toast({ title: 'Success', description: 'User updated successfully' });
      setEditUserOpen(false);
      loadUsers();
    } catch (error: any) {
      console.error('Failed to update user:', error);
      toast({ title: 'Error', description: error.message || 'Failed to update user', variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Delete user
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setIsProcessing(true);
    try {
      const { error: userError } = await supabase
        .from('users')
        .delete()
        .eq('id', selectedUser.id);
      
      if (userError) throw userError;
      
      toast({ title: 'Success', description: 'User deleted successfully' });
      setDeleteUserOpen(false);
      loadUsers();
    } catch (error: any) {
      console.error('Failed to delete user:', error);
      toast({ title: 'Error', description: error.message || 'Failed to delete user', variant: 'destructive' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Toggle user status
  const handleToggleUserStatus = async (targetUser: User) => {
    const newStatus = targetUser.status === 'active' ? 'suspended' : 'active';
    
    try {
      const { error } = await supabase
        .from('users')
        .update({ status: newStatus, updated_at: new Date().toISOString() })
        .eq('id', targetUser.id);
      
      if (error) throw error;
      
      toast({ 
        title: 'Success', 
        description: `User ${newStatus === 'active' ? 'activated' : 'suspended'} successfully` 
      });
      loadUsers();
    } catch (error: any) {
      console.error('Failed to update user status:', error);
      toast({ title: 'Error', description: error.message || 'Failed to update user status', variant: 'destructive' });
    }
  };

  // Reset password for user
  const handleResetPassword = async (targetUser: User) => {
    toast({ title: 'Info', description: `Password reset link would be sent to ${targetUser.email}` });
  };

  // View audit logs for a user
  const handleViewAuditLogs = async (targetUser: User) => {
    setSelectedUserLogs(targetUser);
    await loadAuditLogs(targetUser.id);
    setShowAuditDialog(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      role: 'analyst',
      analyst_type: null,
      password: '',
    });
  };

  // Open edit dialog
  const openEditDialog = (userToEdit: User) => {
    setSelectedUser(userToEdit);
    setFormData({
      email: userToEdit.email,
      name: userToEdit.name,
      role: userToEdit.role,
      analyst_type: userToEdit.analyst_type,
      password: '',
    });
    setEditUserOpen(true);
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    if (roleFilter !== 'all' && u.role !== roleFilter) return false;
    if (statusFilter !== 'all' && u.status !== statusFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        u.name.toLowerCase().includes(query) ||
        u.email.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Load system stats
  const loadSystemStats = useCallback(async () => {
    try {
      const { data: storageData } = await supabase.storage.listBuckets();
      const storageUsed = storageData ? `${storageData.length * 10} MB` : '0 MB';
      
      const { count: apiCount } = await supabase
        .from('audit_logs')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', new Date().toISOString().split('T')[0]);
      
      setSystemStats(prev => ({
        ...prev,
        storageUsed,
        apiCallsToday: apiCount || 0,
      }));
    } catch (error) {
      console.error('Failed to load system stats:', error);
    }
  }, []);

  const refreshAllData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        loadUsers(),
        loadSystemStats(),
        refreshUsers(),
      ]);
      toast({ title: 'Refreshed', description: 'Dashboard data updated' });
    } catch (error) {
      console.error('Refresh failed:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [loadUsers, loadSystemStats, refreshUsers, toast]);

  // Setup real-time subscription
  useEffect(() => {
    loadUsers();
    loadSystemStats();
    
    const channel = supabase
      .channel('admin_user_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'users' }, () => {
        setIsRealtimeConnected(true);
        loadUsers();
      })
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsRealtimeConnected(true);
        } else if (status === 'CHANNEL_ERROR') {
          setIsRealtimeConnected(false);
        }
      });
    
    return () => {
      supabase.removeChannel(channel);
    };
  }, [loadUsers, loadSystemStats]);

  // Polling fallback
  useEffect(() => {
    const interval = setInterval(() => {
      if (!isRealtimeConnected) {
        loadUsers();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [isRealtimeConnected, loadUsers]);

  const getRoleBadge = (role: string) => {
    const roleConfig = USER_ROLES.find(r => r.value === role);
    if (!roleConfig) return <Badge variant="outline">{role}</Badge>;
    const Icon = roleConfig.icon;
    return (
      <Badge className={`${roleConfig.color} border-0`}>
        <Icon className="h-3 w-3 mr-1" />
        {roleConfig.label}
      </Badge>
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700 border-0"><CheckCircle className="h-3 w-3 mr-1" />Active</Badge>;
      case 'inactive':
        return <Badge className="bg-gray-100 text-gray-700 border-0"><Clock className="h-3 w-3 mr-1" />Inactive</Badge>;
      case 'suspended':
        return <Badge className="bg-red-100 text-red-700 border-0"><XCircle className="h-3 w-3 mr-1" />Suspended</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  // Access denied for non-admin users
  if (user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-screen">
        <Card className="max-w-md w-full mx-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <ShieldAlert className="h-6 w-6" />
              Access Denied
            </CardTitle>
            <CardDescription>
              You do not have permission to access the Admin Dashboard.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/analyst-dashboard')} className="w-full">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full">
      {/* Header Section - KEPT but with no extra padding/margin creating blank space */}
      <div className="mb-6 px-6">
        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-purple-600" />
              <h1 className="text-2xl font-bold tracking-tight">Admin Dashboard</h1>
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              User Management & System Administration
            </p>
            <div className="flex items-center gap-3 mt-2">
              <div className="flex items-center gap-1">
                {isRealtimeConnected ? (
                  <>
                    <Wifi className="h-3 w-3 text-green-500" />
                    <span className="text-xs text-green-600">Live Updates Active</span>
                  </>
                ) : (
                  <>
                    <WifiOff className="h-3 w-3 text-red-500" />
                    <span className="text-xs text-red-600">Reconnecting...</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={refreshAllData} disabled={isRefreshing} size="sm">
              <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6 px-6">
        <StatCard title="Total Users" value={systemStats.totalUsers.toString()} description="All registered users" icon={Users} color="bg-blue-500" />
        <StatCard title="Active" value={systemStats.activeUsers.toString()} description="Currently active" icon={UserCheck} color="bg-green-500" />
        <StatCard title="Suspended" value={systemStats.suspendedUsers.toString()} description="Access blocked" icon={UserX} color="bg-red-500" />
        <StatCard title="Admins" value={systemStats.admins.toString()} description="System administrators" icon={Shield} color="bg-purple-500" />
        <StatCard title="Analysts" value={systemStats.analysts.toString()} description="Business & Problem Bank" icon={UserCog} color="bg-indigo-500" />
        <StatCard title="Founders" value={systemStats.founders.toString()} description="Startup founders" icon={Briefcase} color="bg-emerald-500" />
      </div>

      {/* USER MANAGEMENT VIEW */}
      {activeView === 'users' && (
        <div className="px-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  System Users
                </CardTitle>
                <CardDescription>
                  Manage user accounts, roles, and permissions
                </CardDescription>
              </div>
              <Button onClick={() => { resetForm(); setCreateUserOpen(true); }} className="bg-green-600 hover:bg-green-700">
                <UserPlus className="h-4 w-4 mr-2" />
                Create New User
              </Button>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex flex-wrap gap-3 mb-5">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Select value={roleFilter} onValueChange={setRoleFilter}>
                  <SelectTrigger className="w-36">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Roles</SelectItem>
                    {USER_ROLES.map(role => (
                      <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="suspended">Suspended</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Users Table */}
              {isLoadingUsers ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : filteredUsers.length === 0 ? (
                <div className="text-center py-12">
                  <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">No users found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Role</TableHead>
                        <TableHead>Analyst Type</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Last Login</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((u) => (
                        <TableRow key={u.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-8 w-8">
                                <AvatarFallback className="text-xs">{getInitials(u.name)}</AvatarFallback>
                              </Avatar>
                              <div>
                                <p className="font-medium">{u.name}</p>
                                <p className="text-xs text-muted-foreground">{u.email}</p>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{getRoleBadge(u.role)}</TableCell>
                          <TableCell>
                            {u.analyst_type === 'business' && <Badge variant="outline" className="bg-emerald-50">Business Analyst</Badge>}
                            {u.analyst_type === 'problem_bank' && <Badge variant="outline" className="bg-amber-50">Problem Bank Analyst</Badge>}
                            {!u.analyst_type && u.role !== 'analyst' && u.role !== 'senior_analyst' && <span className="text-muted-foreground text-sm">—</span>}
                            {!u.analyst_type && (u.role === 'analyst' || u.role === 'senior_analyst') && <Badge variant="outline" className="bg-gray-50">Not Specified</Badge>}
                          </TableCell>
                          <TableCell>{getStatusBadge(u.status)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(u.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openEditDialog(u)}
                                title="Edit User"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleToggleUserStatus(u)}
                                title={u.status === 'active' ? 'Suspend User' : 'Activate User'}
                                className={u.status === 'active' ? 'text-yellow-600' : 'text-green-600'}
                              >
                                {u.status === 'active' ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleResetPassword(u)}
                                title="Reset Password"
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleViewAuditLogs(u)}
                                title="View Audit Logs"
                              >
                                <FileText className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => { setSelectedUser(u); setDeleteUserOpen(true); }}
                                title="Delete User"
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* SYSTEM SETTINGS VIEW */}
      {activeView === 'system' && (
        <div className="px-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 p-3 rounded-lg">
                    <Database className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Storage Used</p>
                    <p className="text-xl font-bold">{systemStats.storageUsed}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="bg-green-100 p-3 rounded-lg">
                    <Activity className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">API Calls Today</p>
                    <p className="text-xl font-bold">{systemStats.apiCallsToday.toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <Server className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">System Version</p>
                    <p className="text-xl font-bold">v{systemStats.version}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="bg-orange-100 p-3 rounded-lg">
                    <HardDrive className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Uptime</p>
                    <p className="text-xl font-bold">99.9%</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="h-5 w-5" />
                System Configuration
              </CardTitle>
              <CardDescription>
                Global system settings and preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <h3 className="text-sm font-medium text-muted-foreground">User Settings</h3>
                <div className="flex items-center justify-between py-2 border-b">
                  <div>
                    <p className="font-medium">Allow New Registrations</p>
                    <p className="text-sm text-muted-foreground">Allow founders to register new accounts</p>
                  </div>
                  <Switch defaultChecked />
                </div>
                <div className="flex items-center justify-between py-2 border-b">
                  <div>
                    <p className="font-medium">Email Verification Required</p>
                    <p className="text-sm text-muted-foreground">Require email verification for new accounts</p>
                  </div>
                  <Switch defaultChecked />
                </div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button variant="outline">
                  <Save className="h-4 w-4 mr-2" />
                  Save Settings
                </Button>
                <Button variant="ghost">Reset to Defaults</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* AUDIT LOGS VIEW */}
      {activeView === 'audit' && (
        <div className="px-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                System Audit Logs
              </CardTitle>
              <CardDescription>
                Track all administrative actions and system events
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoadingLogs ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                  <p className="text-muted-foreground">No audit logs available</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {auditLogs.slice(0, 50).map((log) => (
                    <div key={log.id} className="p-3 bg-muted/30 rounded-lg border-l-4 border-l-purple-500">
                      <div className="flex justify-between items-start flex-wrap gap-2">
                        <div>
                          <p className="font-medium text-sm">{log.action.replace(/_/g, ' ').toUpperCase()}</p>
                          <p className="text-sm text-muted-foreground">{log.details}</p>
                          <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs text-muted-foreground">By: {log.user_name || 'System'}</span>
                          </div>
                        </div>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(log.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* CREATE USER DIALOG */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>Add a new user to the system</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="John Doe"
              />
            </div>
            <div className="space-y-2">
              <Label>Email Address *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="user@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Password *</Label>
              <Input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
              />
              <p className="text-xs text-muted-foreground">Minimum 6 characters</p>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(formData.role === 'analyst' || formData.role === 'senior_analyst') && (
              <div className="space-y-2">
                <Label>Analyst Type</Label>
                <Select 
                  value={formData.analyst_type || 'null'} 
                  onValueChange={(value) => setFormData({ ...formData, analyst_type: value === 'null' ? null : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select analyst type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="null">Not Specified</SelectItem>
                    <SelectItem value="business">Business Analyst</SelectItem>
                    <SelectItem value="problem_bank">Problem Bank Analyst</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateUserOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateUser} disabled={isProcessing} className="bg-green-600 hover:bg-green-700">
              {isProcessing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              {isProcessing ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT USER DIALOG */}
      <Dialog open={editUserOpen} onOpenChange={setEditUserOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update user information and role</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={formData.email} disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground">Email cannot be changed</p>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {USER_ROLES.map(role => (
                    <SelectItem key={role.value} value={role.value}>{role.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {(formData.role === 'analyst' || formData.role === 'senior_analyst') && (
              <div className="space-y-2">
                <Label>Analyst Type</Label>
                <Select 
                  value={formData.analyst_type || 'null'} 
                  onValueChange={(value) => setFormData({ ...formData, analyst_type: value === 'null' ? null : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select analyst type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="null">Not Specified</SelectItem>
                    <SelectItem value="business">Business Analyst</SelectItem>
                    <SelectItem value="problem_bank">Problem Bank Analyst</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUserOpen(false)}>Cancel</Button>
            <Button onClick={handleUpdateUser} disabled={isProcessing} className="bg-purple-600 hover:bg-purple-700">
              {isProcessing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {isProcessing ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* DELETE USER ALERT DIALOG */}
      <AlertDialog open={deleteUserOpen} onOpenChange={setDeleteUserOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete User</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedUser?.name}? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
              {isProcessing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              {isProcessing ? 'Deleting...' : 'Delete User'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AUDIT LOGS DIALOG */}
      <Dialog open={showAuditDialog} onOpenChange={setShowAuditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Logs: {selectedUserLogs?.name}</DialogTitle>
            <DialogDescription>User activity history</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {isLoadingLogs ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : auditLogs.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-30" />
                <p className="text-muted-foreground">No audit logs found for this user</p>
              </div>
            ) : (
              auditLogs.map((log) => (
                <div key={log.id} className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-sm">{log.action.replace(/_/g, ' ').toUpperCase()}</p>
                      <p className="text-sm text-muted-foreground">{log.details}</p>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap ml-3">
                      {new Date(log.created_at).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAuditDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}